import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from 'react'
import { apiFetch } from './api'

export type AdminUserRole = 'USER' | 'MANAGER' | 'ADMIN'

interface AuthUser {
  id: string
  email: string
  name: string | null
  image: string | null
  role: AdminUserRole
  aiCredits: number
  /** MANAGER 의 권한 그룹 (FIKET-417). ADMIN/USER 는 null */
  adminRole: { id: string; name: string } | null
  /** MANAGER 의 메뉴 권한 { 메뉴키: ['view'|'edit'] }. ADMIN 은 전권이라 null */
  adminGrants: Record<string, string[]> | null
}

interface AuthContextValue {
  user: AuthUser | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (provider: 'kakao' | 'naver' | 'google') => void
  logout: () => Promise<void>
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  // 마운트당 1회 보장 — prod에서도 useEffect가 두 번 fire되는 현상 차단.
  // api.ts의 401→refresh→retry 로직이 추가로 증폭하는 것까지 방지.
  const didFetchRef = useRef(false)

  const fetchUser = useCallback(async () => {
    try {
      const data = await apiFetch<AuthUser>('/auth/me')
      setUser(data)
    } catch {
      setUser(null)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    if (didFetchRef.current) return
    didFetchRef.current = true
    fetchUser()
  }, [fetchUser])

  const login = useCallback((provider: 'kakao' | 'naver' | 'google') => {
    window.location.href = `/auth/${provider}?return_to=${encodeURIComponent(window.location.origin)}`
  }, [])

  const logout = useCallback(async () => {
    setUser(null)
    try {
      await apiFetch('/auth/logout', { method: 'POST' })
    } catch {
      // 무시
    }
  }, [])

  const refreshUser = useCallback(async () => {
    await fetchUser()
  }, [fetchUser])

  return (
    <AuthContext value={{ user, isAuthenticated: !!user, isLoading, login, logout, refreshUser }}>
      {children}
    </AuthContext>
  )
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
